import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogIn, User, ShoppingBag, Shield, LogOut } from "lucide-react";

export default function HeaderBar({ onSignIn, dark = false }) {
    const { user, logout } = useAuth();

    const textCls = dark ? "text-white" : "text-navy";
    const initials = user?.name
        ? user.name
              .split(" ")
              .filter(Boolean)
              .slice(0, 2)
              .map((p) => p[0]?.toUpperCase() || "")
              .join("")
        : "";

    return (
        <div
            data-testid="header-bar"
            className="absolute top-0 left-0 right-0 z-30 px-5 sm:px-8 pt-8 sm:pt-10 pb-4 flex items-center justify-between"
        >
            <Link
                to="/"
                data-testid="header-home-link"
                className="flex items-center"
            >
                <img
                    src="https://customer-assets.emergentagent.com/job_haiti-flavors-boston/artifacts/d6079zc1_file_00000000e4f071f8926f38a90a1f87a6.png"
                    alt="Caribbean Meal Prep"
                    className="h-14 sm:h-16 w-auto"
                />
            </Link>

            {user ? (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="header-account-btn"
                            className="flex items-center gap-2 rounded-full bg-white/90 hover:bg-white pl-1 pr-4 py-1 border border-mint shadow-sm transition-all active:scale-95"
                        >
                            {user.picture ? (
                                <img
                                    src={user.picture}
                                    alt={user.name}
                                    className="w-8 h-8 rounded-full object-cover"
                                />
                            ) : (
                                <span className="w-8 h-8 rounded-full bg-forest text-white font-bold text-xs flex items-center justify-center">
                                    {initials || "🌴"}
                                </span>
                            )}
                            <span className="text-navy text-sm font-bold max-w-[120px] truncate">
                                {user.name?.split(" ")[0] || "Account"}
                            </span>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="w-56 rounded-2xl border-mint"
                    >
                        <DropdownMenuLabel className="font-serif">
                            <div className="font-bold text-navy">
                                {user.name}
                            </div>
                            <div className="text-xs text-muted2 font-normal truncate">
                                {user.email}
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild data-testid="menu-account">
                            <Link to="/account" className="flex items-center gap-2 cursor-pointer">
                                <ShoppingBag size={16} /> My Orders
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild data-testid="menu-profile">
                            <Link to="/account#profile" className="flex items-center gap-2 cursor-pointer">
                                <User size={16} /> Profile
                            </Link>
                        </DropdownMenuItem>
                        {user.is_admin && (
                            <DropdownMenuItem asChild data-testid="menu-admin">
                                <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-forest font-bold">
                                    <Shield size={16} /> Admin Dashboard
                                </Link>
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            data-testid="menu-logout"
                            onClick={logout}
                            className="text-burnt cursor-pointer flex items-center gap-2"
                        >
                            <LogOut size={16} /> Sign out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ) : (
                <Button
                    data-testid="header-signin-btn"
                    onClick={onSignIn}
                    className="rounded-full bg-gold hover:bg-goldDark text-navy font-bold px-5 py-2 h-auto flex items-center gap-2"
                >
                    <LogIn size={16} /> Sign in
                </Button>
            )}
        </div>
    );
}
